import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { apiUrl } from "../auth/config";
import { useAuth } from "../auth/AuthContext";
import "../styles/app.css";
import "../styles/proofs.css";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "proof", label: "Proof (active)" },
  { value: "queued", label: "Queued" },
  { value: "approved", label: "Approved" },
  { value: "sidelined", label: "Sidelined" },
  { value: "declined", label: "Declined" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

/** Reviewer filter — each person is pointed at their practice queue(s). */
const REVIEWER_FILTERS = [
  { value: "", label: "All queues" },
  { value: "walt", label: "Walt — Real estate" },
  { value: "matt", label: "Matt — Injury + Estate" },
];

const PRACTICE_QUEUE_ORDER = ["real-estate", "injury", "estate"];
const PRACTICE_QUEUE_LABELS = {
  "real-estate": "Real estate",
  injury: "Injury",
  estate: "Estate",
};

const LEGACY_REVIEWER_STORAGE_KEY = "ftoj.proofs.reviewer";

function formatRunOutDate(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return isoDate;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function loginEmail(user) {
  return (
    user?.signInDetails?.loginId ||
    user?.username ||
    ""
  )
    .toString()
    .trim()
    .toLowerCase();
}

/** Walt + synapsis (everyone else) → walt; Matt → matt. */
function defaultReviewerForLogin(email) {
  if (email === "mjones@jonesboothlaw.com") return "matt";
  return "walt";
}

export default function Proofs() {
  const { user, loading, isAuthenticated, isAdmin, logout, getIdToken } =
    useAuth();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [status, setStatus] = useState("proof");
  const [reviewer, setReviewer] = useState("walt");
  const [error, setError] = useState("");
  const [info, setInfo] = useState(location.state?.flash || "");
  const [fetching, setFetching] = useState(true);
  const appliedLoginRef = useRef("");

  // Default from login once per account (synapsis → Walt). Dropdown can peer;
  // do not persist that over the login default (old localStorage broke this).
  useEffect(() => {
    if (!user) return;
    const email = loginEmail(user);
    if (!email || appliedLoginRef.current === email) return;
    appliedLoginRef.current = email;
    setReviewer(defaultReviewerForLogin(email));
    try {
      localStorage.removeItem(LEGACY_REVIEWER_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [user]);

  const load = useCallback(async () => {
    setFetching(true);
    setError("");
    try {
      const token = await getIdToken();
      const qs = status ? `?status=${encodeURIComponent(status)}` : "";
      const res = await fetch(`${apiUrl}/proofs${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError("Admin role required to manage blog proofs.");
        setPosts([]);
        return;
      }
      if (!res.ok) throw new Error(`Could not load proofs (${res.status})`);
      const data = await res.json();
      setPosts(Array.isArray(data.posts) ? data.posts : []);
      setPipeline(data.pipeline || data.buffer || null);
    } catch (err) {
      setError(err.message || "Failed to load proofs");
      setPosts([]);
    } finally {
      setFetching(false);
    }
  }, [getIdToken, status]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    load();
  }, [isAuthenticated, isAdmin, load]);

  const visiblePosts = useMemo(() => {
    if (!reviewer) return posts;
    return posts.filter((p) => p.reviewerId === reviewer);
  }, [posts, reviewer]);

  const runOutLabel = formatRunOutDate(pipeline?.runOutDate);
  const approved = pipeline?.approved ?? null;

  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <p className="leads-support">Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/signin" replace />;

  if (!isAdmin) {
    return (
      <div className="app-shell">
        <header className="app-header">
          <Link className="app-header__brand" to="/">
            FTOJ
          </Link>
          <div className="app-header__meta">
            <Link to="/leads">Inbox</Link>
            <button className="btn btn--ghost" type="button" onClick={() => logout()}>
              Sign out
            </button>
          </div>
        </header>
        <main className="app-main">
          <section className="leads-panel">
            <h2>Admin only</h2>
            <p>
              Blog proofs require an <strong>admin</strong> account. Ask an
              operator to promote your user if you need access.
            </p>
            <Link to="/leads">← Back to inbox</Link>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link className="app-header__brand" to="/">
          FTOJ
        </Link>
        <div className="app-header__meta">
          <Link to="/leads">Inbox</Link>
          <Link to="/proofs">Proofs</Link>
          <Link to="/security">Security</Link>
          <span>{user?.signInDetails?.loginId || user?.username}</span>
          <button className="btn btn--ghost" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="leads-heading">
          <div>
            <h1 className="leads-title">Blog proofs</h1>
            <p className="leads-support">
              Three practice queues (real estate, injury, estate) — up to 5
              active proofs each, filled from the queued backlog. Approve to
              stock the publish runway (1 per day). Your login defaults the
              filter; you can still peer into other queues.
            </p>
          </div>
          <button className="btn btn--ghost" type="button" onClick={load} disabled={fetching}>
            Refresh
          </button>
        </div>

        {pipeline ? (
          <section className="proof-runway" aria-label="Publish runway">
            <div className="proof-runway__stat">
              <p className="proof-runway__label">Approved</p>
              <p className="proof-runway__value">
                {approved == null ? "—" : approved}
              </p>
              <p className="proof-runway__hint">ready to publish</p>
            </div>
            <div className="proof-runway__stat">
              <p className="proof-runway__label">Runway ends</p>
              <p className="proof-runway__value proof-runway__value--date">
                {approved === 0
                  ? "Empty now"
                  : runOutLabel || pipeline.runOutDate || "—"}
              </p>
              <p className="proof-runway__hint">at 1 post / day</p>
            </div>
            {PRACTICE_QUEUE_ORDER.map((id) => {
              const q = pipeline.queues?.[id];
              const target = q?.target ?? pipeline.targetPerQueue ?? 5;
              const proof = q?.proof ?? 0;
              const queued = q?.queued ?? 0;
              const label = q?.queueLabel || PRACTICE_QUEUE_LABELS[id];
              return (
                <div className="proof-runway__stat" key={id}>
                  <p className="proof-runway__label">{label}</p>
                  <p className="proof-runway__value">
                    {proof}/{target}
                  </p>
                  <p className="proof-runway__hint">
                    active · {queued} queued
                    {proof < target && queued === 0 ? " · seed more" : ""}
                  </p>
                </div>
              );
            })}
          </section>
        ) : null}

        <div className="proof-filters">
          <label>
            Reviewer
            <select
              value={reviewer}
              onChange={(e) => setReviewer(e.target.value)}
            >
              {REVIEWER_FILTERS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_FILTERS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}
        {info ? <p className="auth-info">{info}</p> : null}
        {fetching ? <p className="leads-support">Loading…</p> : null}

        {!fetching && visiblePosts.length === 0 ? (
          <section className="leads-panel">
            <h2>No posts</h2>
            <p>
              {reviewer
                ? "Nothing in this reviewer’s queues for the selected status. Try All queues or another status."
                : "Seed a sample with "}
              {!reviewer ? (
                <>
                  <code>./scripts/seed-blog-proof.sh staging</code> or POST{" "}
                  <code>/proofs</code>.
                </>
              ) : null}
            </p>
          </section>
        ) : null}

        <ul className="proof-list">
          {visiblePosts.map((p) => (
            <li key={p.postId}>
              <Link className="proof-row" to={`/proofs/${p.postId}`}>
                <div>
                  <p className="proof-row__title">{p.title}</p>
                  <p className="proof-row__meta">
                    {p.queueLabel || "Queue"} · {p.category} · {p.slug}
                  </p>
                </div>
                <span className={`proof-status proof-status--${p.status}`}>
                  {p.status}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
